import ApartmentService from '@/services/ApartmentService';
import ResimoService from '@/services/ResimoService';
import { Breakpoint, ExtendedWindow } from '@/models/app.model';
import { AppConfig, PanoramaMappings } from '@/models/app-config.model';
import { MarkersData, TransformedMarkersData } from '@/models/markers-data.model';
import { verQuery } from '@/helpers/assetsVersionForCache';
import MapService from './MapService';
import RouteService from './RouteService';
import ViewerService from './ViewerService';
import MarkersConfigTransformer from '../transformers/MarkersConfigTransformer';

class AppService {
    markersConfig: null | MarkersData;

    transformedMarkersConfig: null | TransformedMarkersData;

    mapService: MapService | null;

    viewerService: ViewerService | null;

    routeService: RouteService;

    apartmentService: ApartmentService;

    resimoService: ResimoService;

    breakpoint: Breakpoint;

    static appConfig = (window as unknown as ExtendedWindow).__APP_CONFIG__ as AppConfig;

    assetsDirectoryName: string | null;

    resimoDataLoadError: boolean;

    apartmentNotFoundError: boolean;

    panoramaMappingsLoadError: boolean;

    markersConfigLoadError: boolean;

    public constructor(breakpoint: Breakpoint) {
        this.resimoDataLoadError = false;
        this.apartmentNotFoundError = false;
        this.panoramaMappingsLoadError = false;
        this.markersConfigLoadError = false;
        this.markersConfig = null;
        this.mapService = null;
        this.viewerService = null;
        this.transformedMarkersConfig = null;
        this.routeService = new RouteService();
        this.apartmentService = new ApartmentService();
        this.resimoService = new ResimoService();
        this.breakpoint = breakpoint;
        this.assetsDirectoryName = null;
    }

    public async setupMarkersConfig() {
        await AppService.fetchPanoramaMappingsData().then((response) => {
            const matched = response.PanoramaMappings
                .find((el) => el.Apartments
                    .find((name) => name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                    === this.apartmentService.apartment.mappingName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')));

            if (!matched) {
                this.apartmentNotFoundError = true;
            } else {
                this.assetsDirectoryName = matched.DirectoryName;
            }
        }).catch(() => { this.panoramaMappingsLoadError = true; });

        return this.fetchMarkersConfig()
            .then((config: MarkersData) => {
                this.markersConfig = config;

                const transformedMarkersConfig = MarkersConfigTransformer.transform(
                    this.markersConfig,
                    `${AppService.getApartmentsPath()}/${this.assetsDirectoryName}`,
                    this.breakpoint,
                    AppService.appConfig.hasWebpImages,
                );

                this.transformedMarkersConfig = transformedMarkersConfig;
                this.mapService = new MapService(transformedMarkersConfig, AppService.appConfig.hasWebpImages);
                this.viewerService = new ViewerService(transformedMarkersConfig);
            }).catch(() => { this.markersConfigLoadError = true; });
    }

    public async setupApartmentService() {
        return this.resimoService.fetchData(
            AppService.getApiEndpoint(),
            this.routeService.apartmentSymbol ? this.routeService.apartmentSymbol : '',
        ).then((apartment) => {
            if (apartment != null) {
                this.apartmentService.setData(apartment);
            } else {
                this.apartmentNotFoundError = true;
            }
        }).catch(() => { this.resimoDataLoadError = true; });
    }

    private static fetchPanoramaMappingsData() {
        return fetch(AppService.getApartmentMappingDataJsonPath())
            .then((response) => response.json() as Promise<PanoramaMappings>);
    }

    private fetchMarkersConfig() {
        const markersPath = `${AppService.getApartmentsPath()}/${
            this.assetsDirectoryName
        }/MarkersData.json?${verQuery}`;
        return fetch(markersPath).then((response) => response.json());
    }

    public static getAssetsEndpoint() {
        return this.appConfig?.endpoints.assets || '';
    }

    public static getApartmentMappingDataJsonPath() {
        return (
            `${this.getAssetsEndpoint()}/${this.appConfig?.endpoints.panoramaMappingsFile}?${verQuery}` || ''
        );
    }

    public static getApartmentsPath() {
        return `${this.appConfig?.endpoints.assets}/apartments` || '';
    }

    public static getApiEndpoint() {
        return this.appConfig?.endpoints.api || '';
    }

    public setActiveRoom(roomLabel: string) {
        this.viewerService?.setActiveRoom(roomLabel);
        this.mapService?.setActivePoint(roomLabel);
    }

    public getMapPoints() {
        return this.mapService?.getPoints() || [];
    }

    public getMapData() {
        return this.mapService?.getMapData(AppService.getApartmentsPath());
    }

    public getViewAreaRotateDegOffset() {
        return this.viewerService?.getActiveRoom()?.currentRotation || 0;
    }

    public getCompassRotateDegOffset() {
        return this.transformedMarkersConfig?.northAngle || 0;
    }
}

export default AppService;
